import * as THREE from "three";

/**
 * CSP-safe text/QR textures drawn on a 2D canvas (system fonts only — no remote
 * font fetch, so they work under our strict `connect-src 'self'` CSP, unlike
 * drei's <Text>). Used for in-scene signage ("Restaurant Luigi"), menu/QR
 * stickers, sticky notes, etc.
 */

interface TextOpts {
  width?: number;
  height?: number;
  bg?: string;
  color?: string;
  font?: string;
  align?: CanvasTextAlign;
  padding?: number;
  /** draw a rounded plaque background when bg is set. */
  radius?: number;
  /** extra sub-line under the main text. */
  sub?: string;
  subColor?: string;
  subFont?: string;
}

export function makeTextTexture(text: string, opts: TextOpts = {}): THREE.CanvasTexture {
  const w = opts.width ?? 1024;
  const h = opts.height ?? 256;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  if (opts.bg) {
    g.fillStyle = opts.bg;
    if (opts.radius) {
      roundRect(g, 0, 0, w, h, opts.radius);
      g.fill();
    } else {
      g.fillRect(0, 0, w, h);
    }
  }
  g.fillStyle = opts.color ?? "#ffffff";
  g.textBaseline = "middle";
  g.textAlign = opts.align ?? "center";
  g.font = opts.font ?? `bold ${Math.floor(h * 0.5)}px Georgia, "Times New Roman", serif`;
  const x = opts.align === "left" ? (opts.padding ?? 24) : opts.align === "right" ? w - (opts.padding ?? 24) : w / 2;
  const y = opts.sub ? h * 0.42 : h / 2;
  g.fillText(text, x, y);
  if (opts.sub) {
    g.fillStyle = opts.subColor ?? "#cbd5e1";
    g.font = opts.subFont ?? `${Math.floor(h * 0.2)}px Arial, sans-serif`;
    g.fillText(opts.sub, x, h * 0.74);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** A believable (non-functional) QR sticker: random module grid + finder eyes +
 *  a "Scan for Menu" caption — what's printed on the restaurant table. */
export function makeQrTexture(caption = "Scan for Menu"): THREE.CanvasTexture {
  const s = 512;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s + 120;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#ffffff";
  g.fillRect(0, 0, cv.width, cv.height);

  const N = 25;
  const pad = 48;
  const cell = (s - pad * 2) / N;
  g.fillStyle = "#0a0a0a";
  // deterministic-ish pseudo-random modules
  let seed = 1337;
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (inFinder(r, c, N)) continue;
      if (rnd() > 0.52) g.fillRect(pad + c * cell, pad + r * cell, cell + 0.5, cell + 0.5);
    }
  }
  drawFinder(g, pad, pad, cell);
  drawFinder(g, pad + (N - 7) * cell, pad, cell);
  drawFinder(g, pad, pad + (N - 7) * cell, cell);

  g.fillStyle = "#0a0a0a";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.font = "bold 56px Arial, sans-serif";
  g.fillText(caption, cv.width / 2, s + 60);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/**
 * An "Anonymous hacking group" wall poster: a stylised Guy-Fawkes mask on a dark
 * ground with the slogan "WE HACK THE WORLD" + "WE ARE LEGION · EXPECT US".
 * Pure canvas (no image fetch) so it works under our strict CSP. Reuse for any
 * scene that needs to read instantly as a hacker den.
 */
export function makeHackerPosterTexture(): THREE.CanvasTexture {
  const w = 768;
  const h = 1024;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#0c1018");
  grad.addColorStop(1, "#05070a");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // faint circuit-ish flecks
  let s = 4242;
  const rnd = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  g.fillStyle = "rgba(57,255,154,0.05)";
  for (let i = 0; i < 90; i++) g.fillRect(rnd() * w, rnd() * h, 2, 2);
  g.strokeStyle = "#1f2a3a";
  g.lineWidth = 6;
  g.strokeRect(20, 20, w - 40, h - 40);

  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillStyle = "#e8edf2";
  g.font = 'bold 66px Georgia, "Times New Roman", serif';
  g.fillText("ANONYMOUS", w / 2, 112);

  drawAnonMask(g, w / 2, 440, 250);

  g.fillStyle = "#39ff9a";
  g.font = "bold 82px Georgia, serif";
  g.fillText("WE HACK", w / 2, 770);
  g.fillText("THE WORLD", w / 2, 862);
  g.fillStyle = "#9fb0bd";
  g.font = "28px Arial, sans-serif";
  g.fillText("WE ARE LEGION  \u00b7  EXPECT US", w / 2, 948);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function drawAnonMask(g: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  g.save();
  g.translate(cx, cy);
  const W = size;
  const H = size * 1.25;
  // pale face: rounded top, pointed chin
  g.beginPath();
  g.moveTo(0, -H * 0.5);
  g.bezierCurveTo(W * 0.62, -H * 0.5, W * 0.6, H * 0.05, W * 0.32, H * 0.28);
  g.bezierCurveTo(W * 0.16, H * 0.42, W * 0.06, H * 0.5, 0, H * 0.5);
  g.bezierCurveTo(-W * 0.06, H * 0.5, -W * 0.16, H * 0.42, -W * 0.32, H * 0.28);
  g.bezierCurveTo(-W * 0.6, H * 0.05, -W * 0.62, -H * 0.5, 0, -H * 0.5);
  g.closePath();
  g.fillStyle = "#f2ead8";
  g.fill();
  g.strokeStyle = "#cbbfa6";
  g.lineWidth = 3;
  g.stroke();
  // rosy cheeks
  g.fillStyle = "rgba(214,118,118,0.28)";
  g.beginPath();
  g.ellipse(-W * 0.34, H * 0.07, W * 0.12, H * 0.055, 0, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.ellipse(W * 0.34, H * 0.07, W * 0.12, H * 0.055, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "#1a1a1a";
  // eyes
  g.beginPath();
  g.ellipse(-W * 0.22, -H * 0.12, W * 0.12, H * 0.05, 0.3, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.ellipse(W * 0.22, -H * 0.12, W * 0.12, H * 0.05, -0.3, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = "#1a1a1a";
  g.lineCap = "round";
  // eyebrows
  g.lineWidth = 8;
  g.beginPath();
  g.moveTo(-W * 0.36, -H * 0.23);
  g.lineTo(-W * 0.08, -H * 0.16);
  g.stroke();
  g.beginPath();
  g.moveTo(W * 0.36, -H * 0.23);
  g.lineTo(W * 0.08, -H * 0.16);
  g.stroke();
  // upward-curled moustache
  g.lineWidth = 10;
  g.beginPath();
  g.moveTo(0, H * 0.07);
  g.quadraticCurveTo(-W * 0.18, H * 0.02, -W * 0.27, -H * 0.07);
  g.stroke();
  g.beginPath();
  g.moveTo(0, H * 0.07);
  g.quadraticCurveTo(W * 0.18, H * 0.02, W * 0.27, -H * 0.07);
  g.stroke();
  // smile + goatee
  g.lineWidth = 6;
  g.beginPath();
  g.moveTo(-W * 0.16, H * 0.13);
  g.quadraticCurveTo(0, H * 0.21, W * 0.16, H * 0.13);
  g.stroke();
  g.beginPath();
  g.moveTo(0, H * 0.17);
  g.lineTo(0, H * 0.34);
  g.stroke();
  g.beginPath();
  g.moveTo(-W * 0.06, H * 0.3);
  g.lineTo(0, H * 0.43);
  g.lineTo(W * 0.06, H * 0.3);
  g.stroke();
  g.restore();
}

/**
 * A fake "security feed" screen — CCTV grid / code scroll / netflow graph /
 * signal bars (chosen by `seed`), green-on-black with scanlines. Render on a
 * monitor plane (meshBasicMaterial, unlit) for a wall of glowing surveillance
 * screens. Reuse anywhere you need a SOC / hacker monitor bank.
 */
export function makeSecurityFeedTexture(seed = 1): THREE.CanvasTexture {
  const w = 512;
  const h = 320;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  let s = seed * 99991 + 7;
  const rnd = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  g.fillStyle = "#05080a";
  g.fillRect(0, 0, w, h);
  const green = "#39ff9a";
  const amber = "#ffb347";
  const red = "#ff4d5e";
  const kind = Math.floor(rnd() * 4);

  if (kind === 0) {
    // CCTV 2x2
    [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ].forEach(([cx, cy], i) => {
      const x = 8 + cx * (w / 2 - 4);
      const y = 8 + cy * (h / 2 - 4);
      const cw = w / 2 - 16;
      const ch = h / 2 - 16;
      g.fillStyle = `rgb(${(18 + rnd() * 20) | 0},${(20 + rnd() * 22) | 0},${(24 + rnd() * 22) | 0})`;
      g.fillRect(x, y, cw, ch);
      for (let n = 0; n < 70; n++) {
        g.fillStyle = `rgba(255,255,255,${rnd() * 0.05})`;
        g.fillRect(x + rnd() * cw, y + rnd() * ch, 2, 2);
      }
      g.strokeStyle = "#1c3a2e";
      g.strokeRect(x, y, cw, ch);
      g.fillStyle = green;
      g.font = "bold 14px monospace";
      g.textAlign = "left";
      g.textBaseline = "top";
      g.fillText(`CAM ${(i + 1 + Math.floor(rnd() * 9)).toString().padStart(2, "0")}`, x + 6, y + 6);
      g.fillStyle = red;
      g.beginPath();
      g.arc(x + cw - 12, y + 12, 4, 0, Math.PI * 2);
      g.fill();
    });
  } else if (kind === 1) {
    // code scroll
    g.font = "12px monospace";
    g.textAlign = "left";
    g.textBaseline = "top";
    const hex = "0123456789abcdef";
    for (let row = 0; row < 22; row++) {
      let line = "";
      const len = (40 + rnd() * 20) | 0;
      for (let i = 0; i < len; i++) line += hex[(rnd() * 16) | 0];
      g.fillStyle = rnd() > 0.85 ? amber : green;
      g.globalAlpha = 0.35 + rnd() * 0.6;
      g.fillText(line, 8, 8 + row * 14);
    }
    g.globalAlpha = 1;
    g.fillStyle = green;
    g.font = "bold 16px monospace";
    g.fillText("> exfil 412GB ...", 8, h - 22);
  } else if (kind === 2) {
    // netflow graph
    g.strokeStyle = "#15412f";
    for (let i = 0; i < 8; i++) {
      g.beginPath();
      g.moveTo(0, (i * h) / 8);
      g.lineTo(w, (i * h) / 8);
      g.stroke();
    }
    const nodes = Array.from({ length: 11 }, () => [20 + rnd() * (w - 40), 20 + rnd() * (h - 40)]);
    g.strokeStyle = green;
    g.globalAlpha = 0.5;
    nodes.forEach((a) =>
      nodes.forEach((b) => {
        if (rnd() > 0.86) {
          g.beginPath();
          g.moveTo(a[0], a[1]);
          g.lineTo(b[0], b[1]);
          g.stroke();
        }
      })
    );
    g.globalAlpha = 1;
    nodes.forEach((n, i) => {
      g.fillStyle = i === 0 ? red : green;
      g.beginPath();
      g.arc(n[0], n[1], 4, 0, Math.PI * 2);
      g.fill();
    });
    g.fillStyle = amber;
    g.font = "bold 14px monospace";
    g.textAlign = "left";
    g.textBaseline = "top";
    g.fillText("NETFLOW", 10, 8);
  } else {
    // signal bars
    g.fillStyle = green;
    g.font = "bold 14px monospace";
    g.textAlign = "left";
    g.textBaseline = "top";
    g.fillText("SIGNAL", 10, 8);
    const bars = 28;
    const bw = (w - 20) / bars;
    for (let i = 0; i < bars; i++) {
      const bh = 20 + rnd() * (h - 70);
      g.fillStyle = i % 7 === 0 ? amber : green;
      g.globalAlpha = 0.85;
      g.fillRect(10 + i * bw, h - 20 - bh, bw * 0.7, bh);
    }
    g.globalAlpha = 1;
  }

  // scanlines
  g.fillStyle = "rgba(0,0,0,0.16)";
  for (let y = 0; y < h; y += 3) g.fillRect(0, y, w, 1);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function inFinder(r: number, c: number, n: number): boolean {
  return (r < 8 && c < 8) || (r < 8 && c >= n - 8) || (r >= n - 8 && c < 8);
}

function drawFinder(g: CanvasRenderingContext2D, x: number, y: number, cell: number) {
  g.fillStyle = "#0a0a0a";
  g.fillRect(x, y, cell * 7, cell * 7);
  g.fillStyle = "#ffffff";
  g.fillRect(x + cell, y + cell, cell * 5, cell * 5);
  g.fillStyle = "#0a0a0a";
  g.fillRect(x + cell * 2, y + cell * 2, cell * 3, cell * 3);
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}
